/**
 *
 * "Provides the sideliners as group of 2D boxes in mobile display version
 *  calculated from deck revisions data from database"
 *
 * @file   mobileDeckCanvas\SideLiner.js
 * @author Lateral
 * @since  2023
 */

import { Box, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import BigNumber from 'bignumber.js'
import { DeckMode } from 'common/deckMode'
import { getWearColour, pickHeatColour } from 'common/heatColours'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { useMaintenance } from 'hooks'
import React, { forwardRef } from 'react'

const Item = ({
  maintenance,
  heatMapData,
  item,
  kits,
  materialSelected,
  mode,
  scale,
  selected,
  onClick,
  type,
  inMaintenancePage,
  maintenanceView
}) => {
  const ref = React.useRef(null)
  const isSelected =
    selected.filter(
      (s) => s.MaterialNumber == item.MaterialNumber && s.StartPosition == item.StartPosition && s.Side == item.Side
    ).length > 0

  const { actions, getAction } = useMaintenance()
  const theme = useTheme()

  const interactionColour = '#1441F5'
  const emptyColour = theme.palette.primary.dark
  const isMaterialSelected = materialSelected == item.MaterialNumber

  const kit = kits.find((k) => k.MaterialNumber === item.MaterialNumber)
  const height = `${new BigNumber(item.Height).multipliedBy(scale).toNumber()}em`
  const width = `${scale}em`

  const { currentMaintenanceView } = useCurrentDeckContext()

  let colour =
    isSelected || isMaterialSelected
      ? interactionColour
      : currentMaintenanceView !== 'layout' && inMaintenancePage && (mode !== DeckMode.default || type !== 'report')
      ? emptyColour
      : mode.includes(DeckMode.maintenance) && currentMaintenanceView !== 'layout'
      ? emptyColour
      : kit?.Colour ?? emptyColour

  let maintenanceAction = null
  let detailValue = null
  let isWearMeasurement = false

  //if in Maintenance mode, change colours
  if (
    maintenance &&
    ((currentMaintenanceView && inMaintenancePage) ||
      (mode.includes(DeckMode.maintenance) && (type === 'report' || !isSelected)))
  ) {
    const startPosition = new BigNumber(item.StartPosition)
    const detail = maintenance?.Details?.find(
      (d) => startPosition.isEqualTo(d.SideLiner?.StartPosition) && item.Side === d.SideLiner?.Side
    )

    if (detail) {
      maintenanceAction = getAction(detail.HistoryAction)
      if (
        (mode === DeckMode.maintenance && maintenanceAction.id === actions.NoChange.id) ||
        (mode === DeckMode.preMaintenance &&
          (((detail.WorkingDepth || detail.WorkingDepth === 0) &&
            (detail.ApertureWidth || detail.ApertureWidth === 0)) ||
            (detail.DepthPercentage && detail.DepthPercentage !== 0))) ||
        (currentMaintenanceView && inMaintenancePage)
      ) {
        const previousColor = colour
        isWearMeasurement = detail.WearAppResponse === 'add-wear-action'
        colour = getWearColour(detail, theme)
        if (currentMaintenanceView === 'mm') {
          detailValue = detail.WorkingDepth ?? null
        } else if (currentMaintenanceView === '%') {
          detailValue = detail.DepthPercentage == null ? null : Math.round(Number(detail.DepthPercentage))
        } else if (currentMaintenanceView === 'action' || maintenanceView === 'action') {
          colour = emptyColour
        } else if (currentMaintenanceView === 'layout') {
          // colour = kit?.Colour ?? emptyColour
        }
        if (
          (detailValue == null && (currentMaintenanceView || maintenanceView) && inMaintenancePage) ||
          !(currentMaintenanceView || maintenanceView)
        ) {
          if (mode !== DeckMode.preMaintenance) colour = previousColor
          detailValue = detail.DepthPercentage ?? null
        } else if (currentMaintenanceView === 'action' || maintenanceView === 'action') {
          colour = emptyColour
        }
        if (detailValue == null && (currentMaintenanceView || maintenanceView) && inMaintenancePage) {
          detailValue = null
        }
        if ((currentMaintenanceView === '%' || currentMaintenanceView === 'mm') && detailValue == null) {
          colour = emptyColour
        }
      } else if (mode !== DeckMode.preMaintenance && maintenanceAction.id !== actions.NoChange.id) {
        if (
          (mode === DeckMode.maintenance && currentMaintenanceView === undefined) ||
          currentMaintenanceView !== 'action'
        ) {
          colour = currentMaintenanceView === 'layout' ? kit?.Colour : isSelected ? interactionColour : emptyColour
        } else {
          colour = 'white'
        }
      }
    }
  } else if (mode === DeckMode.heatMap) {
    const sideLinerHeatData = heatMapData.SideLiners.find(
      (s) => s.Side === item.Side && s.StartPosition === item.StartPosition
    )

    const wearCount = new BigNumber(sideLinerHeatData.WearCount)
    const total = heatMapData.Total.isEqualTo(0) ? new BigNumber(1) : heatMapData.Total
    const heatCount = total.minus(wearCount).dividedBy(total).times(100).toNumber()
    colour = pickHeatColour(heatCount, theme)
  }

  const backgroundColour = type === 'report' && mode === DeckMode.postMaintenance ? emptyColour : colour

  let child = (
    <Box
      sx={{
        backgroundColor: `${backgroundColour}`,
        border: '1px solid black',
        width: { width },
        height: { height },
        '&:hover': {
          backgroundColor: interactionColour
        }
      }}>
      {(type === 'report' ? mode === DeckMode.preMaintenance : currentMaintenanceView !== 'action') &&
        detailValue !== null && (
          <Typography
            color="black"
            sx={{
              fontSize: '8px'
            }}
            variant="body1">
            {detailValue}
          </Typography>
        )}

      {currentMaintenanceView === 'action' && maintenanceAction && !isWearMeasurement && (
        <img
          src={`${maintenanceAction.svg}`}
          style={{
            border: '1px solid black',
            height: `${height}`,
            width: `${width}`,
            verticalAlign: 'top'
          }}
        />
      )}
    </Box>
  )

  if (
    ((type === 'report' && [DeckMode.maintenance, DeckMode.default, DeckMode.postMaintenance].includes(mode)) ||
      currentMaintenanceView === 'action') &&
    !isSelected &&
    maintenanceAction &&
    maintenanceAction.id !== actions.NoChange.id
  ) {
    child = (
      <img
        src={`${maintenanceAction.svg}`}
        style={{
          border: '1px solid black',
          height: `${height}`,
          width: `${width}`,
          verticalAlign: 'top'
        }}
      />
    )
  }

  return (
    <Box
      sx={{
        margin: 0,
        height
      }}
      data-panel-type="SideLiners"
      data-selection={isSelected}
      data-item={JSON.stringify(item)}
      ref={ref}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        onClick(item, e)
        return true
      }}>
      {child}
    </Box>
  )
}

/*
 *
 */
export const SideLiner = forwardRef(function SideLiner_(
  {
    data,
    kits,
    maintenance,
    materialSelected,
    onPanelClick,
    scale,
    side,
    mode,
    presentation,
    heatMapData,
    selected,
    type,
    inMaintenancePage,
    maintenanceView
  },
  ref
) {
  /**
   * Generates a Sideliner boxes based on incoming deck rivion data.
   * @function
   * @param {object} data - Deck rivision object from database
   * @param {object} maintenance - Deck Revision Histories
   * @param {object[]} kits - Item details including material details of panels and sideliners
   * @param {number} materialSelected - Selected sideliner material number
   * @param {} onPanelClick - Action to click on a panel/sideliners. Same action applicable for panels and sideliners
   * @param {object[]} selected - Array of selected panel objects
   * @param {number} scale - Scale of the canvas
   * @param {string} side - Which side Left?Right.
   * @param {boolean} presentation - In maintenance mode presentation = True to show high contrast texts
   * @param {string} mode - Deck modes:   'default', 'edit', 'maintenance', 'heatmap', 'maintnance', 'pre-maintenance', 'post-maintenance'
   * @returns {object} - React group of 2D boxes
   */

  const left = side === 'left' ? '0.2em' : '1em'
  const right = side === 'right' ? '0' : '1em'

  return (
    <Stack
      id="test"
      justifyContent="center"
      alignItems="center"
      sx={{
        marginLeft: `${left}`,
        marginRight: `${right}`,
        height: 'fit-content',
        width: 'fit-content',
        userSelect: 'none'
      }}>
      <Typography fontSize={12} sx={[{ width: '100%', textAlign: 'center' }, presentation ? { color: 'black' } : null]}>
        {side === 'left' ? 'RS' : 'LS'}
      </Typography>
      <div ref={ref}>
        {data.map((item, index) => {
          return (
            <Item
              key={`${item.Side}-${item.StartPosition}-${index}`}
              maintenance={maintenance}
              heatMapData={heatMapData}
              item={item}
              kits={kits}
              materialSelected={materialSelected}
              mode={mode}
              onClick={onPanelClick}
              scale={scale}
              selected={selected}
              type={type}
              inMaintenancePage={inMaintenancePage}
              maintenanceView={maintenanceView}
            />
          )
        })}
      </div>
    </Stack>
  )
})
